import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Campaign as CampaignIcon,
  Chat as ChatIcon,
  PlayArrow as PlayArrowIcon,
  QrCode2 as QrCode2Icon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  StopCircle as StopCircleIcon,
} from '@mui/icons-material';
import {
  WhatsAppRecentChatItem,
  WhatsAppQueueItem,
  WhatsAppQueueBatchStatus,
  WhatsAppWebNumber,
  createNumber,
  disconnectNumberSession,
  enqueueCampaignQueue,
  getNumberQr,
  getNumberSessionStatus,
  getQueueBatchStatus,
  listConversations,
  listNumbers,
  listQueueItems,
  listRecentChats,
  processQueue,
  startNumberSession,
} from '../services/whatsappWebService';
import { Campaign, listCampaigns } from '../services/campaignManualService';
import { getLeadStatusChip } from '../utils/leadStatusChip';

const DEFAULT_FORM = {
  label: '',
  phone_number: '',
  daily_limit: 40,
  minute_limit: 6,
  is_default: false,
};

const WhatsAppWeb: React.FC = () => {
  const [numbers, setNumbers] = useState<WhatsAppWebNumber[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [queueItems, setQueueItems] = useState<WhatsAppQueueItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [processingQueue, setProcessingQueue] = useState(false);
  const [batchStatus, setBatchStatus] = useState<WhatsAppQueueBatchStatus | null>(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | ''>('');
  const [selectedSenderNumberId, setSelectedSenderNumberId] = useState<number | ''>('');
  const [enqueueLimit, setEnqueueLimit] = useState('100');
  const [enqueuing, setEnqueuing] = useState(false);
  const [qrDialog, setQrDialog] = useState({
    open: false,
    numberId: null as number | null,
    status: '',
    qrCode: '',
    detail: '',
  });
  const [recentChatsDialog, setRecentChatsDialog] = useState({
    open: false,
    numberLabel: '',
    items: [] as WhatsAppRecentChatItem[],
    loading: false,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  });

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const connectedNumbers = useMemo(
    () => numbers.filter((number) => number.status === 'connected'),
    [numbers]
  );

  const queueStats = useMemo(() => {
    return {
      pending: queueItems.filter((item) => item.status === 'pending').length,
      sending: queueItems.filter((item) => item.status === 'sending').length,
      failed: queueItems.filter((item) => item.status === 'failed').length,
    };
  }, [queueItems]);

  const loadDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [numbersData, campaignsData, queueData, unreadConversations] = await Promise.all([
        listNumbers(),
        listCampaigns(),
        listQueueItems(),
        listConversations({ unread_only: true, limit: 200 }),
      ]);
      setNumbers(numbersData);
      setCampaigns(campaignsData);
      setQueueItems(queueData);
      setUnreadCount(unreadConversations.reduce((acc, conversation) => acc + conversation.unread_count, 0));
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Erro ao carregar painel do WhatsApp Web', 'error');
    }
    setLoading(false);
  }, []);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    if (!qrDialog.open || !qrDialog.numberId) {
      return;
    }

    let cancelled = false;
    let timeoutId: number | undefined;

    const pollQr = async () => {
      try {
        const [statusData, qrData] = await Promise.all([
          getNumberSessionStatus(qrDialog.numberId!),
          getNumberQr(qrDialog.numberId!),
        ]);
        if (cancelled) return;
        setQrDialog((prev) => ({
          ...prev,
          status: statusData.status,
          qrCode: qrData.qr_code || '',
          detail: qrData.detail || '',
        }));
        if (statusData.status === 'connected') {
          loadDashboardData();
        }
      } catch (error: any) {
        if (cancelled) return;
        setQrDialog((prev) => ({
          ...prev,
          detail: error.response?.data?.detail || 'Erro ao atualizar QR Code',
        }));
      } finally {
        if (!cancelled) {
          timeoutId = window.setTimeout(pollQr, 4000);
        }
      }
    };

    pollQr();
    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [qrDialog.open, qrDialog.numberId, loadDashboardData]);

  const getStatusChip = (status: string) => {
    const normalized = status?.toLowerCase?.() || '';
    if (normalized === 'connected') return <Chip label="Conectado" color="success" size="small" />;
    if (normalized === 'qr_pending') return <Chip label="Aguardando QR" color="warning" size="small" />;
    if (normalized === 'starting') return <Chip label="Iniciando" color="info" size="small" />;
    if (normalized === 'disconnected') return <Chip label="Desconectado" color="default" size="small" />;
    if (normalized === 'error') return <Chip label="Erro" color="error" size="small" />;
    return <Chip label={status} size="small" />;
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('pt-BR');
  };

  const handleCreateNumber = async () => {
    if (!form.label.trim()) {
      showSnackbar('Informe um nome interno para o número', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const created = await createNumber({
        ...form,
        label: form.label.trim(),
        phone_number: form.phone_number.trim() || undefined,
      });
      setForm(DEFAULT_FORM);
      showSnackbar('Número cadastrado! Iniciando conexão e gerando QR Code...', 'success');
      await loadDashboardData();
      // "Cadastrar" sozinho só grava nome/limites -- sem isso o usuário teria
      // que descobrir separadamente o botão "Conectar" na tabela pra realmente
      // conectar o WhatsApp. Encadeia os dois num fluxo só.
      await handleStartSession(created);
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Erro ao cadastrar número', 'error');
    }
    setSubmitting(false);
  };

  const handleStartSession = async (number: WhatsAppWebNumber) => {
    try {
      const result = await startNumberSession(number.id);
      setQrDialog({
        open: true,
        numberId: number.id,
        status: result.status,
        qrCode: '',
        detail: result.detail,
      });
      showSnackbar('Sessão iniciada. Aguarde o QR Code carregar.', 'info');
      await loadDashboardData();
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Erro ao iniciar sessão', 'error');
    }
  };

  const handleDisconnectSession = async (number: WhatsAppWebNumber) => {
    try {
      await disconnectNumberSession(number.id);
      showSnackbar('Sessão desconectada com sucesso', 'success');
      await loadDashboardData();
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Erro ao desconectar sessão', 'error');
    }
  };

  const handleOpenQr = async (number: WhatsAppWebNumber) => {
    setQrDialog({
      open: true,
      numberId: number.id,
      status: number.status,
      qrCode: '',
      detail: 'Carregando QR Code...',
    });
    try {
      const [statusData, qrData] = await Promise.all([
        getNumberSessionStatus(number.id),
        getNumberQr(number.id),
      ]);
      setQrDialog({
        open: true,
        numberId: number.id,
        status: statusData.status,
        qrCode: qrData.qr_code || '',
        detail: qrData.detail || '',
      });
    } catch (error: any) {
      setQrDialog((prev) => ({
        ...prev,
        detail: error.response?.data?.detail || 'QR Code indisponível no momento',
      }));
    }
  };

  const handleOpenRecentChats = async (number: WhatsAppWebNumber) => {
    setRecentChatsDialog({
      open: true,
      numberLabel: number.label,
      items: [],
      loading: true,
    });
    try {
      const data = await listRecentChats(number.id, false);
      setRecentChatsDialog({
        open: true,
        numberLabel: number.label,
        items: data.items,
        loading: false,
      });
    } catch (error: any) {
      setRecentChatsDialog({
        open: true,
        numberLabel: number.label,
        items: [],
        loading: false,
      });
      showSnackbar(error.response?.data?.detail || 'Erro ao carregar conversas recentes', 'error');
    }
  };

  const handleEnqueueCampaign = async () => {
    if (selectedCampaignId === '') {
      showSnackbar('Selecione uma campanha para enfileirar', 'warning');
      return;
    }
    const limitValue = enqueueLimit.trim() ? parseInt(enqueueLimit, 10) : undefined;
    if (enqueueLimit.trim() && (!limitValue || limitValue < 1)) {
      showSnackbar('Quantidade a enfileirar deve ser um número maior que zero', 'warning');
      return;
    }
    setEnqueuing(true);
    try {
      const result = await enqueueCampaignQueue(selectedCampaignId as number, {
        sender_number_id: selectedSenderNumberId === '' ? undefined : Number(selectedSenderNumberId),
        skip_already_sent: true,
        limit: limitValue,
      });
      showSnackbar(
        `Fila atualizada: ${result.queued} lead(s) enfileirado(s) e ${result.skipped} pulado(s)`,
        'success'
      );
      await loadDashboardData();
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Erro ao enfileirar campanha', 'error');
    }
    setEnqueuing(false);
  };

  const handleProcessQueue = async () => {
    const senderId = selectedSenderNumberId === '' ? undefined : Number(selectedSenderNumberId);
    setProcessingQueue(true);
    try {
      const started = await processQueue(senderId);
      if (started.already_running) {
        showSnackbar('Já existe um processamento da fila em andamento.', 'info');
      } else {
        showSnackbar('Processando a fila em segundo plano (1 envio a cada 1-3min)...', 'info');
      }

      // A fila inteira processa no backend, um envio real por vez, respeitando o
      // intervalo anti-ban -- pode levar bastante tempo (dezenas de minutos numa
      // fila grande). Em vez de segurar a request (estourava o timeout do axios),
      // só consultamos o progresso periodicamente até o lote terminar.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        if (!isMountedRef.current) return;

        const status = await getQueueBatchStatus(senderId);
        setBatchStatus(status);
        await loadDashboardData();

        if (!status.running) {
          const reason = status.stop_reason ? ` (${status.stop_reason})` : '';
          showSnackbar(
            `Lote concluído: ${status.sent} envio(s), ${status.cancelled} cancelado(s), ${status.failed} falha(s)${reason}`,
            status.failed > 0 ? 'warning' : 'success'
          );
          break;
        }
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.detail || 'Erro ao processar fila', 'error');
    }
    if (isMountedRef.current) {
      setProcessingQueue(false);
    }
  };

  const renderQrContent = () => {
    if (qrDialog.status === 'connected') {
      return <Alert severity="success">Número conectado com sucesso. O QR Code não é mais necessário.</Alert>;
    }
    if (qrDialog.status === 'error') {
      return (
        <Alert severity="error">
          {qrDialog.detail || 'Falha ao iniciar a sessão do WhatsApp Web. Tente novamente.'}
        </Alert>
      );
    }
    if (!qrDialog.qrCode) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={220}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (qrDialog.qrCode.startsWith('data:image')) {
      return (
        <Box display="flex" justifyContent="center">
          <Box
            component="img"
            src={qrDialog.qrCode}
            alt="QR Code do WhatsApp"
            sx={{ width: 260, height: 260, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
          />
        </Box>
      );
    }
    return (
      <Alert severity="info">
        O backend retornou um identificador de QR em vez de imagem renderizável. Valor: <strong>{qrDialog.qrCode}</strong>
      </Alert>
    );
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          WhatsApp Web
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Conecte números por QR Code, acompanhe a fila e distribua campanhas entre sessões ativas.
        </Typography>
      </Box>

      <Alert severity="warning" sx={{ mb: 3 }}>
        Use poucos disparos por número e monitore a saúde das sessões. Este módulo foi pensado para operação controlada.
      </Alert>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Números cadastrados</Typography>
              <Typography variant="h4">{numbers.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Números conectados</Typography>
              <Typography variant="h4">{connectedNumbers.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Fila pendente</Typography>
              <Typography variant="h4">{queueStats.pending}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Mensagens não lidas</Typography>
              <Typography variant="h4">{unreadCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button startIcon={<RefreshIcon />} variant="outlined" onClick={loadDashboardData} disabled={loading}>
          Atualizar painel
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Novo número
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Nome interno"
                value={form.label}
                onChange={(event) => setForm((prev) => ({ ...prev, label: event.target.value }))}
                size="small"
                fullWidth
              />
              <TextField
                label="Telefone (opcional)"
                value={form.phone_number}
                onChange={(event) => setForm((prev) => ({ ...prev, phone_number: event.target.value }))}
                size="small"
                fullWidth
                helperText="Usado para identificação e normalização do número"
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Limite diário"
                    type="number"
                    value={form.daily_limit}
                    onChange={(event) => setForm((prev) => ({ ...prev, daily_limit: Number(event.target.value || 0) }))}
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Limite/min"
                    type="number"
                    value={form.minute_limit}
                    onChange={(event) => setForm((prev) => ({ ...prev, minute_limit: Number(event.target.value || 0) }))}
                    size="small"
                    fullWidth
                  />
                </Grid>
              </Grid>
              <Button
                variant="contained"
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                onClick={handleCreateNumber}
                disabled={submitting}
              >
                Cadastrar número
              </Button>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Campanhas e fila
            </Typography>
            <Stack spacing={2}>
              <FormControl size="small" fullWidth>
                <InputLabel id="campaign-select-label">Campanha</InputLabel>
                <Select
                  labelId="campaign-select-label"
                  label="Campanha"
                  value={selectedCampaignId}
                  onChange={(event: SelectChangeEvent<number | string>) =>
                    setSelectedCampaignId(event.target.value === '' ? '' : Number(event.target.value))
                  }
                >
                  <MenuItem value="">
                    <em>Selecione</em>
                  </MenuItem>
                  {campaigns.map((campaign) => (
                    <MenuItem key={campaign.id} value={campaign.id}>
                      {campaign.name} ({campaign.total_leads} leads)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel id="sender-number-select-label">Número remetente</InputLabel>
                <Select
                  labelId="sender-number-select-label"
                  label="Número remetente"
                  value={selectedSenderNumberId}
                  onChange={(event: SelectChangeEvent<number | string>) =>
                    setSelectedSenderNumberId(event.target.value === '' ? '' : Number(event.target.value))
                  }
                >
                  <MenuItem value="">
                    <em>Distribuir automaticamente</em>
                  </MenuItem>
                  {connectedNumbers.map((number) => (
                    <MenuItem key={number.id} value={number.id}>
                      {number.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Quantidade a enfileirar"
                helperText="Deixe vazio para tentar enfileirar tudo de uma vez (pode travar em campanhas com milhares de leads)"
                size="small"
                type="number"
                fullWidth
                value={enqueueLimit}
                onChange={(e) => setEnqueueLimit(e.target.value)}
              />

              <Button
                variant="contained"
                startIcon={enqueuing ? <CircularProgress size={18} color="inherit" /> : <CampaignIcon />}
                onClick={handleEnqueueCampaign}
                disabled={enqueuing}
              >
                Enfileirar campanha
              </Button>
              <Button
                variant="outlined"
                startIcon={processingQueue ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                onClick={handleProcessQueue}
                disabled={processingQueue}
              >
                Processar fila agora
              </Button>
              <Alert severity="info">
                Use a fila para disparo controlado. O processador respeita número conectado, limites e log de mensagens.
              </Alert>
              {processingQueue && batchStatus && (
                <Alert severity="info">
                  Em andamento: {batchStatus.sent} enviado(s), {batchStatus.cancelled} cancelado(s),{' '}
                  {batchStatus.failed} falha(s) até agora. Próximo envio respeita o intervalo de 1-3min.
                </Alert>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Números conectados e sessões
            </Typography>
            <Grid container spacing={2}>
              {numbers.map((number) => (
                <Grid item xs={12} md={6} key={number.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {number.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {number.phone_number || number.normalized_phone || 'Telefone não informado'}
                          </Typography>
                        </Box>
                        {getStatusChip(number.status)}
                      </Box>

                      <Stack spacing={0.5} sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Enviadas hoje: <strong>{number.sent_today_count}</strong> / {number.daily_limit}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Última conexão: {formatDate(number.last_connected_at)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Último health check: {formatDate(number.last_healthcheck_at)}
                        </Typography>
                        {number.last_error && (
                          <Alert severity="error" sx={{ mt: 1 }}>
                            {number.last_error}
                          </Alert>
                        )}
                      </Stack>

                      <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
                        <Button size="small" variant="contained" startIcon={<PlayArrowIcon />} onClick={() => handleStartSession(number)}>
                          Iniciar
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<QrCode2Icon />} onClick={() => handleOpenQr(number)}>
                          QR Code
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<ChatIcon />} onClick={() => handleOpenRecentChats(number)}>
                          Conversas
                        </Button>
                        <Tooltip title={number.status !== 'connected' ? 'Só faz sentido após conectar a sessão' : ''}>
                          <span>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              startIcon={<StopCircleIcon />}
                              onClick={() => handleDisconnectSession(number)}
                              disabled={number.status !== 'connected' && number.status !== 'starting' && number.status !== 'qr_pending'}
                            >
                              Desconectar
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              {numbers.length === 0 && (
                <Grid item xs={12}>
                  <Alert severity="info">Nenhum número cadastrado ainda.</Alert>
                </Grid>
              )}
            </Grid>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Fila operacional
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Campanha</TableCell>
                    <TableCell>Lead</TableCell>
                    <TableCell>Número</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Mensagem</TableCell>
                    <TableCell>Criado em</TableCell>
                    <TableCell>Erro</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queueItems.map((item) => {
                    const campaign = campaigns.find((entry) => entry.id === item.campaign_id);
                    const number = numbers.find((entry) => entry.id === item.sender_number_id);
                    return (
                      <TableRow key={item.id} hover>
                        <TableCell>{item.id}</TableCell>
                        <TableCell>{campaign?.name || item.campaign_id || '-'}</TableCell>
                        <TableCell>{item.lead_nome || item.lead_id}</TableCell>
                        <TableCell>{number?.label || item.sender_number_id || '-'}</TableCell>
                        <TableCell>{getLeadStatusChip(item.lead_status)}</TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>
                          <Typography variant="body2" noWrap>
                            {item.payload?.text || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatDate(item.created_at)}</TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          {item.last_error ? (
                            <Tooltip title={item.last_error}>
                              <Typography variant="body2" color="error" noWrap>
                                {item.last_error}
                              </Typography>
                            </Tooltip>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {queueItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary">
                          Nenhum item na fila ainda.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={qrDialog.open} onClose={() => setQrDialog({ open: false, numberId: null, status: '', qrCode: '', detail: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>QR Code da sessão</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Status atual
              </Typography>
              {getStatusChip(qrDialog.status)}
            </Box>
            {renderQrContent()}
            {qrDialog.status !== 'error' && (
              <Alert severity={qrDialog.status === 'connected' ? 'success' : 'info'}>
                {qrDialog.detail || 'Aguarde o carregamento do QR Code e faça a leitura pelo celular.'}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialog({ open: false, numberId: null, status: '', qrCode: '', detail: '' })}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={recentChatsDialog.open}
        onClose={() => setRecentChatsDialog({ open: false, numberLabel: '', items: [], loading: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Conversas recentes - {recentChatsDialog.numberLabel}</DialogTitle>
        <DialogContent>
          {recentChatsDialog.loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <List>
              {recentChatsDialog.items.map((item, index) => (
                <ListItem key={`${item.title}-${index}`} divider>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                        <Typography variant="body1" fontWeight={600}>
                          {item.title}
                        </Typography>
                        {item.unread_count > 0 && <Chip label={`${item.unread_count} não lidas`} color="warning" size="small" />}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {item.subtitle || 'Sem prévia disponível'}
                        </Typography>
                        {item.last_time && (
                          <Typography variant="caption" color="text.secondary">
                            Último horário: {item.last_time}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
              {recentChatsDialog.items.length === 0 && (
                <ListItem>
                  <ListItemText primary="Nenhuma conversa encontrada para esta sessão." />
                </ListItem>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecentChatsDialog({ open: false, numberLabel: '', items: [], loading: false })}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default WhatsAppWeb;
